import * as Excel from 'exceljs';
import { saveAs } from 'file-saver';
import Moment from 'moment';
import { t } from '@lingui/macro';
import * as ExcelStyles from './excelstyles';
import * as ExcelConstants from './excelconstants';
import { studentStatus as studentAssignmentStatus } from '../../helpers/utils';

export default class DownloadData {
  static generateSheet(wsData, gridTopics, i18n) {
    const workbook = new Excel.Workbook();
    const sheetName = 'Sheet1';
    let sheet = workbook.addWorksheet(sheetName);
    wsData.forEach((topic, key) => {
      const row = sheet.addRow(topic);
      if (key === ExcelConstants.ROW_2) {
        row.font = ExcelStyles.fontWithBold;
      } else if (key === ExcelConstants.ROW_3) {
        row.font = ExcelStyles.fontWithBoldGray;
      } else {
        row.font = ExcelStyles.font;
      }
    });

    sheet = this.formatHeaderRow(gridTopics, sheet);
    sheet = this.formatResultRow(wsData, gridTopics, sheet);
    sheet = this.formatSheet(sheet, wsData, i18n);
    return workbook;
  }

  static formatSheet(sheet, wsData, i18n) {
    sheet.getCell(wsData.length, 1).value = i18n._(
      t('downloadexcel.sheet.classAverage')`Class Average`
    );
    sheet.getCell('A1').font = ExcelStyles.headerFont;
    sheet.getCell(wsData.length, ExcelConstants.CELL_2).value = '';
    sheet.getCell(wsData.length, ExcelConstants.CELL_3).value = '';
    sheet.getCell(wsData.length, ExcelConstants.CELL_3).border =
      ExcelStyles.cellTopBottomBorder;
    sheet.getCell(wsData.length, ExcelConstants.CELL_4).value = '';
    sheet.getColumn(ExcelConstants.COLUMN_1).width =
      ExcelStyles.studentDetailColWidth;
    sheet.getColumn(ExcelConstants.COLUMN_2).width =
      ExcelStyles.studentDetailColWidth;
    sheet.mergeCells('A1:B1');
    sheet.columns.forEach((column, key) => {
      if (key > ExcelConstants.COLUMN_2) column.width = ExcelStyles.columnWidth;
    });
    if (wsData && wsData.length > 0) {
      sheet.getCell(
        wsData.length,
        wsData[wsData.length - ExcelConstants.CELL_1].length
      ).border = ExcelStyles.cellTopBottomRightBorder;
    }
    sheet.getRow(4).height = ExcelStyles.columnHeightTopics;
    sheet.getCell('A4').font = ExcelStyles.subHeaderFont;
    sheet.getCell('B4').font = ExcelStyles.subHeaderFont;
    sheet.getCell('C4').font = ExcelStyles.subHeaderFont;
    return sheet;
  }
  static formatHeaderRow(gridTopics, sheet) {
    // merging topic headers columns
    const mergeCellRow = ExcelConstants.MERGE_CELL_ROW;
    let mergeCellStart = ExcelConstants.MERGE_CELL_START;
    let mergeCellEnd = ExcelConstants.MERGE_CELL_END;
    let cellCounter = ExcelConstants.CELL_COUNTER;
    gridTopics.find((sTopic) => {
      if (sTopic.selected) {
        const activities = sTopic.checkedNormal
          ? sTopic.coreActivities
          : sTopic.easierActivities;
        mergeCellEnd = activities ? activities.length : 0;
        mergeCellEnd += cellCounter;
        sheet.mergeCells(
          mergeCellRow,
          mergeCellStart,
          mergeCellRow,
          mergeCellEnd
        );
        sheet.getCell(mergeCellRow, mergeCellStart).alignment =
          ExcelStyles.mergeCellAlign;
        sheet.getCell(ExcelConstants.ROW_1, mergeCellEnd).border =
          ExcelStyles.cellRightBorder;
        sheet.getCell(ExcelConstants.ROW_2, mergeCellEnd).border =
          ExcelStyles.cellRightBorder;
        sheet.getCell(ExcelConstants.ROW_3, mergeCellEnd).border =
          ExcelStyles.cellRightBorder;
        sheet.getCell(ExcelConstants.ROW_4, mergeCellEnd).border =
          ExcelStyles.cellRightBorder;
        mergeCellStart = mergeCellEnd + ExcelConstants.CELL_1;
        cellCounter = mergeCellEnd;
      }
      return null;
    });
    return sheet;
  }
  static formatResultRow(wsData, gridTopics, sheet) {
    if (wsData.length > ExcelConstants.CELL_4) {
      let mergeCellEnd = ExcelConstants.MERGE_CELL_END;
      let cellCounter = ExcelConstants.CELL_COUNTER;
      wsData.forEach((sDetails, key) => {
        if (sDetails && key > ExcelConstants.ROW_3 && key < wsData.length) {
          gridTopics.find((sTopic) => {
            if (sTopic.selected) {
              const activities = sTopic.checkedNormal
                ? sTopic.coreActivities
                : sTopic.easierActivities;
              mergeCellEnd = activities ? activities.length : 0;
              sheet.getCell(
                key + ExcelConstants.CELL_1,
                mergeCellEnd + cellCounter
              ).border = ExcelStyles.cellRightBorder;
              cellCounter += mergeCellEnd;
              return null;
            }
            return null;
          });
          cellCounter = ExcelConstants.CELL_COUNTER;
          if (key === wsData.length - ExcelConstants.CELL_1) {
            sDetails.forEach((cellDetails, cellIndex) => {
              if (cellDetails) {
                sheet.getCell(
                  key + ExcelConstants.CELL_1,
                  cellIndex + ExcelConstants.CELL_1
                ).border = ExcelStyles.cellTopBottomBorder;
              }
            });
          }
        }
      });
    }
    return sheet;
  }

  static async downloadExcel(
    wsData,
    i18n,
    gridTopics,
    setDownloadProgressFlag
  ) {
    const workbook = this.generateSheet(wsData, gridTopics, i18n);
    // Creating XLSX file
    const fileName = i18n._(t('downloadexcel.filename')`A&R Courses.xlsx`);
    const buffer = await workbook.xlsx.writeBuffer();
    const fileType =
      'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet';
    const blob = new Blob([buffer], { type: fileType });
    saveAs(blob, fileName);
    setDownloadProgressFlag(false);
  }

  static processDataForExcel(
    assignments,
    i18n,
    gridTopics,
    filteredStudents,
    activeClass,
    activeGroup,
    setDownloadProgressFlag
  ) {
    const wsData = [
      [
        i18n._(
          t(
            'downloadexcel.sheet.header'
          )`Course activity assignments and results`
        )
      ],
      [
        i18n._(
          t('downloadexcel.sheet.extractedon')`Extracted ${Moment(
            new Date(),
            'DD/MM/YYY'
          ).format('DD MMM YYYY')}`
        )
      ]
    ];
    const {
      headerRow,
      activityRow,
      studentRow,
      resultRow
    } = this.getHeaderRows(
      assignments,
      i18n,
      gridTopics,
      activeClass,
      activeGroup,
      filteredStudents
    );
    wsData.push(headerRow);
    wsData.push(activityRow);
    studentRow.forEach((studentDetails) => {
      wsData.push(studentDetails);
    });
    wsData.push(resultRow);
    this.downloadExcel(wsData, i18n, gridTopics, setDownloadProgressFlag);
  }

  static getHeaderRows(
    assignments,
    i18n,
    gridTopics,
    activeClass,
    activeGroup,
    filteredStudents
  ) {
    const headerRow = [activeClass, activeGroup, '', ''];
    const activityRow = [
      i18n._(t('downloadexcel.sheet.surnameheader')`Student Surname`),
      i18n._(t('downloadexcel.sheet.nameheader')`Student First Name`),
      i18n._(t('downloadexcel.sheet.assignmentheader')`# Current Assignments`),
      ''
    ];

    gridTopics.find((sTopic) => {
      if (sTopic.selected) {
        const activities = sTopic.checkedNormal
          ? sTopic.coreActivities
          : sTopic.easierActivities;
        activities.forEach((activity, key) => {
          if (key === 0 && activity) {
            headerRow.push(sTopic.topicName);
          } else {
            headerRow.push('');
          }
          activityRow.push(activity.activityName);
        });
      }
      return null;
    });
    const { studentRow, resultRow } = this.getStudentDetails(
      filteredStudents,
      assignments,
      gridTopics
    );
    return {
      headerRow,
      activityRow,
      studentRow,
      resultRow
    };
  }
  static getStudentDetails(filteredStudents, assignments, gridTopics) {
    const studentRow = [];
    let studentDetailsRow = [];

    filteredStudents.map((currentStudent) => {
      studentDetailsRow.push(
        currentStudent.lastName,
        currentStudent.firstName,
        assignments.totalAssignments.filter(
          (assign) =>
            assign.encryptedUserProfileId ===
            currentStudent.encryptedUserProfileId
        )[0]?.assignedActivityTotal || 0,
        ''
      );
      gridTopics.find((sTopic) => {
        if (sTopic.selected) {
          const activities = sTopic.checkedNormal
            ? sTopic.coreActivities
            : sTopic.easierActivities;
          activities.forEach((activity) => {
            const student =
              assignments.studentAssignments &&
              assignments.studentAssignments.find((item) => {
                return (
                  activity.activityId === item.activityId &&
                  activity.taskType === item.taskType &&
                  item.encryptedUserProfileId ===
                    currentStudent.encryptedUserProfileId
                );
              });
            if (student && student.result) {
              studentDetailsRow.push(student.result.score);
            } else if (student && student.assignment) {
              if (
                student.assignment.assignStatus &&
                (student.assignment.assignStatus ===
                  studentAssignmentStatus.ASSIGNED ||
                  student.assignment.assignStatus ===
                    studentAssignmentStatus.INPROGRESS ||
                  student.assignment.assignStatus ===
                    studentAssignmentStatus.PAST_DUE_DATE)
              ) {
                studentDetailsRow.push('A');
              } else {
                studentDetailsRow.push('');
              }
            } else {
              studentDetailsRow.push('');
            }
          });
        }
        return null;
      });
      studentRow.push(studentDetailsRow);
      studentDetailsRow = [];
      return null;
    });
    const resultRow = studentRow.reduce(
      (r, a) => a.map((b, i) => parseInt(r[i] || 0, 10) + b),
      []
    );
    if (resultRow && resultRow.length > 0) {
      resultRow.forEach((result, key) => {
        const studentScores = this.getStudentScores(studentRow, key);
        let count = 0;
        studentScores.forEach((score) => {
          // converting score to integer to check its type to get count of score per student
          if (!isNaN(parseInt(score, 10))) {
            count += 1;
          }
          return null;
        });
        if (result && result > 0 && count > 0) {
          resultRow[key] = parseFloat(result / count).toFixed(1);
        } else {
          resultRow[key] = '0';
        }
      });
    }
    return { studentRow, resultRow };
  }
  static getStudentScores = (studentDetails, id) =>
    studentDetails.map((x) => x[id]);
}



// WEBPACK FOOTER //
// ./helpers/exportexcel/download-excel.js